/**
 * PDF 解析器 — 使用 pdfjs-dist 提取文本并 block 化
 *
 * pdfjs-dist 在浏览器环境需要配置 worker。
 * MVP 使用 inline worker（无需额外文件），通过 workerSrc 指定。
 *
 * 对齐 features.md 1.1：PDF 导入，解析为 Hump Doc
 */

import type { Block } from '@hump/shared-types';
import type { Parser, ParseResult } from './types';
import { createBlock } from './block-builder';
import { extractFormula } from './formula-extractor';
import { isTableRow, extractTable } from './table-extractor';

export class PdfParser implements Parser {
  readonly sourceType = 'pdf' as const;

  async parse(input: ArrayBuffer): Promise<ParseResult> {
    // 复制一份 ArrayBuffer 副本，避免被底层 API detach 后无法重读
    // （pdfjs 可能会 transfer ArrayBuffer 导致原引用失效）
    input = input.slice(0);

    // 动态导入 pdfjs-dist，避免在非 PDF 场景加载
    const pdfjs = await import('pdfjs-dist');

    // 配置 worker：由全局变量 HUMP_PDF_WORKER_SRC 指定（宿主环境设置）
    // 如果未设置，回退到 CDN（与 pdfjs 版本匹配）
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      const workerSrc =
        (typeof globalThis !== 'undefined' && (globalThis as Record<string, unknown>).HUMP_PDF_WORKER_SRC as string) ||
        `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
    }

    let pdf;
    try {
      const loadingTask = pdfjs.getDocument({ data: input });
      pdf = await loadingTask.promise;
    } catch (e) {
      throw new Error(`PDF 解析失败：${(e as Error).message}。请检查文件是否损坏。`);
    }

    const blocks: Block[] = [];
    let order = 0;
    let title = 'Untitled';

    // 从元数据提取标题
    try {
      const meta = await pdf.getMetadata();
      const info = meta?.info as Record<string, unknown> | undefined;
      if (info?.Title && typeof info.Title === 'string' && info.Title.trim()) {
        title = info.Title.trim();
      }
    } catch {
      // 元数据读取失败，使用默认标题
    }

    // 逐页提取文本
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // 按 Y 坐标分组，同一行的文本合并为一个 block
      const lines: Map<number, { text: string; fontSize: number }> = new Map();

      for (const item of textContent.items) {
        if (!('str' in item) || !item.str) continue;

        // transform[5] 是 Y 坐标，用于分行
        const y = Math.round(item.transform[5]);
        const fontSize = item.transform[0] || item.height || 10;

        const existing = lines.get(y);
        if (existing) {
          existing.text += item.str;
          existing.fontSize = Math.max(existing.fontSize, fontSize);
        } else {
          lines.set(y, { text: item.str, fontSize });
        }
      }

      // 按 Y 坐标降序排列（PDF 坐标系 Y 向上）
      const sortedLines = [...lines.entries()].sort((a, b) => b[0] - a[0]);

      // 将连续行合并为段落（空行分段）
      let currentParagraph = '';
      let currentMaxFontSize = 0;
      let tableBuffer: string[] = [];

      const flushParagraph = () => {
        const trimmed = currentParagraph.trim();
        if (!trimmed) {
          currentParagraph = '';
          currentMaxFontSize = 0;
          return;
        }

        // 1. 检测公式（优先级最高）
        const formula = extractFormula(trimmed);
        if (formula) {
          blocks.push(
            createBlock({
              docId: '', order: order++, type: 'formula',
              latex: formula.latex, original: formula.original,
            })
          );
          currentParagraph = '';
          currentMaxFontSize = 0;
          return;
        }

        // 2. 检测表格（连续行含 tab 或 3+ 空格）
        // 累积表格行，遇到非表格行时 flush
        if (isTableRow(trimmed)) {
          tableBuffer.push(trimmed);
          currentParagraph = '';
          currentMaxFontSize = 0;
          return;
        } else if (tableBuffer.length >= 2) {
          const table = extractTable(tableBuffer);
          if (table) {
            blocks.push(
              createBlock({
                docId: '', order: order++, type: 'table',
                tableData: table, original: tableBuffer.join('\n'),
              })
            );
          }
          tableBuffer = [];
        } else if (tableBuffer.length > 0) {
          tableBuffer = [];
        }

        // 大字号视为标题（启发式：fontSize >= 16 且段落不长）
        const isHeading = currentMaxFontSize >= 16 && trimmed.length < 100;

        blocks.push(
          createBlock({
            docId: '',
            order: order++,
            type: isHeading ? 'heading' : 'paragraph',
            level: isHeading ? (currentMaxFontSize >= 24 ? 1 : 2) : undefined,
            original: trimmed,
          })
        );

        // 首个大标题作为文档标题
        if (isHeading && title === 'Untitled' && currentMaxFontSize >= 20) {
          title = trimmed;
        }

        currentParagraph = '';
        currentMaxFontSize = 0;
      }

      for (const [, { text, fontSize }] of sortedLines) {
        const trimmed = text.trim();
        if (!trimmed) {
          // 空行 = 段落分隔
          flushParagraph();
        } else {
          currentParagraph += (currentParagraph ? ' ' : '') + trimmed;
          currentMaxFontSize = Math.max(currentMaxFontSize, fontSize);
        }
      }
      // 页末刷新最后一段
      flushParagraph();

      // 提取页面内嵌图片
      try {
        const images = await this.extractImages(page);
        for (const img of images) {
          // 检查上一个 block 是否是图片标题（"Figure N: ..." 模式）
          const lastBlock = blocks[blocks.length - 1];
          const figMatch = lastBlock?.original?.match(/^(?:Figure|Fig\.?)\s*\d+/i);

          if (figMatch && lastBlock) {
            // 上一个 block 是标题 → 关联到图片，移除标题 block
            const caption = lastBlock.original;
            blocks.pop();
            order--;
            blocks.push(
              createBlock({
                docId: '',
                order: order++,
                type: 'image',
                imageData: img.dataUrl,
                imageWidth: img.width,
                imageHeight: img.height,
                caption,
              })
            );
          } else {
            blocks.push(
              createBlock({
                docId: '',
                order: order++,
                type: 'image',
                imageData: img.dataUrl,
                imageWidth: img.width,
                imageHeight: img.height,
              })
            );
          }
        }
      } catch {
        // 图片提取失败不中断整体解析
      }
    }

    // 扫描版检测：提取出的文本极少 → 可能是扫描版（无文本层）
    // 阈值 <10 更保守，避免误判短文档（如封面、单页票据等含少量文字的合法 PDF）
    const totalChars = blocks.reduce((sum, b) => sum + b.original.length, 0);
    if (pdf.numPages > 3 && totalChars < 10) {
      throw new Error(
        '此 PDF 可能是扫描版（无文本层），无法提取文本。请使用带文本层的 PDF，或等待 OCR 功能上线。'
      );
    }

    return {
      blocks,
      meta: { title, sourceType: 'pdf', lang: 'en' },
    };
  }

  /**
   * 从 PDF 页面提取内嵌图片
   *
   * 通过 operatorList 找到 OPS.paintImageXObject 操作，
   * 获取图片 name，从 page.objs 拿到图片数据，用 canvas 转 dataURL。
   */
  private async extractImages(page: import('pdfjs-dist').PDFPageProxy): Promise<
    Array<{ dataUrl: string; width: number; height: number }>
  > {
    const results: Array<{ dataUrl: string; width: number; height: number }> = [];

    // pdfjs 内部常量（避免完整导入）
    const OPS = (await import('pdfjs-dist')).OPS;

    const operatorList = await page.getOperatorList();
    const imageOps = operatorList.fnArray
      .map((fn, i) => ({ fn, args: operatorList.argsArray[i], index: i }))
      .filter((op) => op.fn === OPS.paintImageXObject || op.fn === OPS.paintImageXObjectRepeat);

    for (const op of imageOps) {
      try {
        const imgName = op.args?.[0] as string;
        if (!imgName || typeof imgName !== 'string') continue;

        // 从 page.objs 获取图片对象（异步）
        const img = await new Promise<unknown>((resolve) => {
          page.objs.get(imgName, resolve);
        });

        if (!img || typeof img !== 'object') continue;

        // pdfjs 图片对象格式：{ width, height, data (Uint8ClampedArray), kind }
        const imgObj = img as { width?: number; height?: number; data?: Uint8ClampedArray | Uint8Array; kind?: number };
        if (!imgObj.width || !imgObj.height || !imgObj.data) continue;

        // 跳过过小的图片（可能是装饰元素/图标）
        if (imgObj.width < 32 || imgObj.height < 32) continue;

        // 用 canvas 将 RGBA 数据转为 PNG dataURL
        const canvas = document.createElement('canvas');
        canvas.width = imgObj.width;
        canvas.height = imgObj.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        const imageData = ctx.createImageData(imgObj.width, imgObj.height);
        // pdfjs data 通常是 RGBA
        const src = imgObj.data;
        const dst = imageData.data;
        if (src.length === dst.length) {
          dst.set(src);
        } else if (src.length === dst.length / 4) {
          // RGB → RGBA
          for (let i = 0, j = 0; i < src.length; i += 3, j += 4) {
            dst[j] = src[i];
            dst[j + 1] = src[i + 1];
            dst[j + 2] = src[i + 2];
            dst[j + 3] = 255;
          }
        } else {
          continue; // 格式不认识，跳过
        }

        ctx.putImageData(imageData, 0, 0);
        // 单独包裹 toDataURL：大图可能因 canvas 面积/内存限制失败，
        // 单张图片失败不应中断整体解析
        let dataUrl: string;
        try {
          dataUrl = canvas.toDataURL('image/png');
        } catch {
          continue;
        }
        results.push({ dataUrl, width: imgObj.width, height: imgObj.height });
      } catch {
        // 单张图片失败跳过
      }
    }

    return results;
  }
}
